<script setup>
/* eslint-disable vue/custom-event-name-casing, vue/no-unused-properties */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import InlineUserPicker from 'dashboard/components-next/pipeline/inline/InlineUserPicker.vue';
import ActivityTab from './tabs/ActivityTab.vue';
import CardSequencesSection from '../CardSequencesSection.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  agents: {
    type: Array,
    default: () => [],
  },
  assignedAgentIds: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['assignAgents', 'itemUpdated']);

const { t } = useI18n();

const assignedAgents = computed(() =>
  props.assignedAgentIds
    .map(id => props.agents.find(a => a.id === id))
    .filter(Boolean)
);

const removeAgent = agentId => {
  const next = props.assignedAgentIds.filter(id => id !== agentId);
  emit('assignAgents', next);
};

const onPickerUpdate = newIds => {
  emit('assignAgents', newIds);
};

// Sequências é cross feature do Pipeline Pro: sem ela, nada de sequência
// aparece no card — nem o bloco colapsável que embrulha a seção.
const { isCloudFeatureEnabled } = useAccount();
const isSequencesEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.PIPELINE_SEQUENCES)
);
</script>

<template>
  <div class="flex flex-col gap-3 w-full">
    <!-- Block 0: Sequences (status, advance step, start new) -->
    <!-- O v-if fica no <details>, não só no conteúdo: `CardSequencesSection` já
         se protege por dentro, mas o cabeçalho colapsável vive AQUI. Sem isto, a
         conta sem a cross feature via um bloco "Sequências" que abre vazio —
         esconder o miolo e deixar a moldura é pior que não esconder nada. -->
    <details
      v-if="isSequencesEnabled"
      open
      class="bg-n-solid-2 border border-n-weak rounded-lg"
    >
      <summary
        class="flex min-h-11 cursor-pointer list-none select-none items-center gap-2 rounded-lg px-4 py-3 transition-colors duration-75 hover:bg-n-alpha-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-n-brand"
      >
        <fluent-icon
          icon="arrow-clockwise"
          size="16"
          class="text-n-slate-10 flex-shrink-0"
        />
        <span class="text-sm font-medium text-n-slate-12 flex-1">
          {{ t('PIPELINE.SEQUENCES.SECTION_TITLE') }}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-n-slate-9"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </summary>
      <div class="px-3 pb-3 pt-1">
        <CardSequencesSection
          :card-id="item.id"
          @sequence-started="emit('itemUpdated')"
        />
      </div>
    </details>

    <!-- Block 1: Activity timeline (open by default) -->
    <details open class="bg-n-solid-2 border border-n-weak rounded-lg">
      <summary
        class="flex min-h-11 cursor-pointer list-none select-none items-center gap-2 rounded-lg px-4 py-3 transition-colors duration-75 hover:bg-n-alpha-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-n-brand"
      >
        <fluent-icon
          icon="arrow-clockwise"
          size="16"
          class="text-n-slate-10 flex-shrink-0"
        />
        <span class="text-sm font-medium text-n-slate-12 flex-1">
          {{ t('PIPELINE.ACTIVITY.TITLE') }}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-n-slate-9"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </summary>
      <div class="px-3 pb-3 pt-1">
        <ActivityTab :item="item" />
      </div>
    </details>

    <!-- Block 2: Assigned agents -->
    <div class="bg-n-solid-2 border border-n-weak rounded-lg">
      <!-- Header -->
      <div class="flex items-center gap-2 px-4 py-3 border-b border-n-weak">
        <!-- People icon -->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-n-slate-10 flex-shrink-0"
          aria-hidden="true"
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
        <span class="text-sm font-medium text-n-slate-12 flex-1">
          {{ t('PIPELINE.ASSIGNEES.TITLE') }}
        </span>
        <span
          v-if="assignedAgentIds.length > 0"
          class="text-xs text-n-slate-10"
        >
          {{ assignedAgentIds.length }}
        </span>
      </div>

      <!-- Agent list -->
      <div class="px-4 py-2">
        <div v-if="assignedAgents.length > 0" class="flex flex-col gap-2 mb-3">
          <div
            v-for="agent in assignedAgents"
            :key="agent.id"
            class="flex items-center gap-3"
          >
            <!-- Avatar with presence dot -->
            <div class="relative flex-shrink-0">
              <Avatar
                :name="agent.name"
                :src="agent.avatar_url"
                :size="32"
                rounded-full
                :status="agent.online === true ? 'online' : null"
              />
            </div>

            <!-- Name + role -->
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ agent.name }}
              </p>
              <p
                v-if="agent.role"
                class="text-xs text-n-slate-10 truncate capitalize"
              >
                {{ agent.role }}
              </p>
            </div>

            <!-- Remove button -->
            <button
              type="button"
              :aria-label="
                t('PIPELINE.ASSIGNEES.REMOVE_AGENT_TITLE', {
                  agent: agent.name,
                })
              "
              :title="
                t('PIPELINE.ASSIGNEES.REMOVE_AGENT_TITLE', {
                  agent: agent.name,
                })
              "
              class="inline-flex min-h-11 min-w-11 flex-shrink-0 items-center justify-center rounded-lg text-n-slate-9 transition-colors duration-75 hover:bg-n-alpha-1 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
              @click="removeAgent(agent.id)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Empty state -->
        <div
          v-if="assignedAgents.length === 0"
          class="flex flex-col items-center gap-2 py-3 text-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-n-slate-8"
            aria-hidden="true"
          >
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
          </svg>
          <p class="text-xs text-n-slate-10">
            {{ t('PIPELINE.ASSIGNEES.EMPTY_STATE') }}
          </p>
        </div>

        <!-- Inline picker -->
        <div class="mb-2">
          <InlineUserPicker
            :model-value="assignedAgentIds"
            :available="agents"
            :show-selected="false"
            multi
            @update:model-value="onPickerUpdate"
          />
        </div>
      </div>
    </div>
  </div>
</template>
